import React, {useState} from 'react';
import {useNavigate} from "react-router-dom";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import {getCustomerById, updateCustomer} from "../../utils/requestUtils";

const ADDRESS_FIELDS = ['deliveryZipCode', 'deliveryStreet', 'deliveryNumber', 'deliveryComplement', 'deliveryReference', 'deliveryNeighborhood'];

function formatZipCode(value) {
    const digits = (value || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) {
        return digits.slice(0, 5) + '-' + digits.slice(5);
    }
    return digits;
}

/**
 * Grava o endereço digitado no cadastro do cliente: direto se o cadastro ainda não tem endereço,
 * perguntando antes se ele for diferente do que já está gravado.
 */
async function updateCustomerAddressIfConfirmed(customerId, deliveryFields) {
    const customer = await getCustomerById(customerId);
    if (!customer) {
        return;
    }
    const hasAddress = ADDRESS_FIELDS.some((field) => (customer[field] || '').trim() !== '');
    const changed = ADDRESS_FIELDS.some((field) => (customer[field] || '').trim() !== (deliveryFields[field] || '').trim());
    if (!changed || (hasAddress && !window.confirm('O endereço é diferente do cadastro do cliente. Deseja alterar o cadastro com este endereço?'))) {
        return;
    }
    const address = Object.fromEntries(ADDRESS_FIELDS.map((field) => [field, deliveryFields[field]]));
    await updateCustomer({...customer, ...address});
}

const SaleManagerDelivery = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useRecoilState(loadingAtom);
    const stored = localStorage.getItem('saleClosing');
    const closingData = stored ? JSON.parse(stored) : null;

    const existingSale = closingData?.sale || {};
    const [zipCode, setZipCode] = useState(existingSale.deliveryZipCode || '');
    const [street, setStreet] = useState(existingSale.deliveryStreet || '');
    const [number, setNumber] = useState(existingSale.deliveryNumber || '');
    const [complement, setComplement] = useState(existingSale.deliveryComplement || '');
    const [reference, setReference] = useState(existingSale.deliveryReference || '');
    const [neighborhood, setNeighborhood] = useState(existingSale.deliveryNeighborhood || '');
    const [fee, setFee] = useState(existingSale.deliveryFee !== undefined && existingSale.deliveryFee !== null ? String(existingSale.deliveryFee) : '');

    if (!closingData) {
        navigate('/app');
        return null;
    }

    const handleZipCodeChange = async (e) => {
        const formatted = formatZipCode(e.target.value);
        setZipCode(formatted);

        const digits = formatted.replace(/\D/g, '');
        if (digits.length === 8) {
            try {
                setLoading(true);
                const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
                const data = await response.json();
                if (!data.erro) {
                    setStreet(data.logradouro ? data.logradouro.toUpperCase() : '');
                    setNeighborhood(data.bairro ? data.bairro.toUpperCase() : '');
                }
            } catch (error) {
                console.error('Error fetching CEP:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const onConfirm = async () => {
        const zipCodeDigits = zipCode.replace(/\D/g, '');
        if (zipCodeDigits.length > 0 && zipCodeDigits.length !== 8) {
            alert('Informe um CEP válido.');
            return;
        }
        if (street.trim() === '') {
            alert('Informe a rua.');
            return;
        }
        if (neighborhood.trim() === '') {
            alert('Informe o bairro.');
            return;
        }
        const parsedFee = fee === '' ? 0 : Number.parseFloat(fee);
        if (Number.isNaN(parsedFee) || parsedFee < 0) {
            alert('Informe um valor de entrega válido.');
            return;
        }

        const deliveryFields = {
            deliveryZipCode: zipCode,
            deliveryStreet: street,
            deliveryNumber: number,
            deliveryComplement: complement,
            deliveryReference: reference,
            deliveryNeighborhood: neighborhood,
            deliveryFee: parsedFee
        };

        if (closingData.sale.customer?.id) {
            try {
                setLoading(true);
                await updateCustomerAddressIfConfirmed(closingData.sale.customer.id, deliveryFields);
            } catch (error) {
                console.error('Error updating customer address:', error);
                alert('Não foi possível alterar o cadastro do cliente. O pedido segue com o endereço informado.');
            } finally {
                setLoading(false);
            }
        }

        const updatedSale = {
            ...closingData.sale,
            ...deliveryFields
        };

        // Recalcula do zero (itens + frete) em vez de somar ao totalValue já salvo, que pode já
        // incluir um frete anterior se o usuário voltou e alterou o valor da entrega.
        const itemsTotal = (closingData.sale.saleItems || [])
            .reduce((acc, item) => acc + (item.product.salePrice * item.quantity), 0);

        localStorage.setItem('saleClosing', JSON.stringify({
            ...closingData,
            sale: updatedSale,
            totalValue: itemsTotal + parsedFee
        }));

        // Também grava no 'sale' base, senão ao voltar pra confirmar itens e reconfirmar,
        // doConfirm() reconstrói o saleClosing do zero a partir dele e perde o endereço já digitado.
        const baseSaleCookie = localStorage.getItem('sale');
        if (baseSaleCookie) {
            const baseSale = JSON.parse(baseSaleCookie);
            localStorage.setItem('sale', JSON.stringify({...baseSale, ...deliveryFields}));
        }

        navigate('/sale/payment');
    };

    return (
        <>
            <div className="header-label">Dados de Entrega</div>

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>CEP (opcional):</div>
                <input type="text" style={{width: '150px'}} value={zipCode} onChange={handleZipCodeChange}
                       inputMode="numeric"/>
            </div>
            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Rua:</div>
                <input type="text" style={{width: '300px'}} value={street}
                       onChange={(e) => setStreet(e.target.value.toUpperCase())}/>
            </div>
            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Número (opcional):</div>
                <input type="text" style={{width: '120px'}} value={number}
                       onChange={(e) => setNumber(e.target.value)}/>
            </div>
            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Complemento:</div>
                <input type="text" style={{width: '300px'}} value={complement}
                       onChange={(e) => setComplement(e.target.value.toUpperCase())}/>
            </div>
            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Ponto de Referência:</div>
                <input type="text" style={{width: '300px'}} value={reference}
                       onChange={(e) => setReference(e.target.value.toUpperCase())}/>
            </div>
            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Bairro:</div>
                <input type="text" style={{width: '300px'}} value={neighborhood}
                       onChange={(e) => setNeighborhood(e.target.value.toUpperCase())}/>
            </div>
            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Valor da Entrega:</div>
                <input type="number" step="0.01" placeholder="0.00" style={{width: '150px'}} value={fee}
                       onChange={(e) => setFee(e.target.value)}/>
            </div>

            <ActionButtonsManagerView>
                <button onClick={() => navigate(-1)} className="back-button">Voltar</button>
                <button onClick={onConfirm} disabled={loading}>Confirmar</button>
            </ActionButtonsManagerView>
        </>
    );
};

export default SaleManagerDelivery;
